import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Plus, Search, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { formatMoney, formatPhone } from '@/lib/format'
import { PhoneLink } from '@/components/PhoneLink'
import { decodeVin, isValidVin, type DecodedVin } from '@/lib/vin'

// Shape returned by GET /api/parts/search. Kept in sync with
// PartsSearchResult in worker/routes/parts.ts.
type PartsSearchResult = {
  source: 'ebay'
  ebay_item_id: string
  title: string
  brand: string | null
  price: number | null
  currency: string
  shipping_cost: number | null
  condition: string | null
  seller: string | null
  image_url: string | null
  item_url: string | null
  is_sandbox: boolean
}

type PartsSearchResponse = {
  env: 'sandbox' | 'production'
  total: number | null
  results: PartsSearchResult[]
}

const HST_RATE = 0.13
const DEFAULT_LABOR_RATE = 100 // GYM Auto's labor rate, see CLAUDE.md.

type CustomerListRow = {
  id: string
  name: string
  phone: string | null
  email: string | null
}

type PartRow = {
  // Stable client-side row id so React keys stay sane across reorders.
  rowKey: string
  name: string
  supplier: string
  price: string
  qty: string
}

function newRowKey(): string {
  return crypto.randomUUID().slice(0, 8)
}

function emptyPartRow(): PartRow {
  return { rowKey: newRowKey(), name: '', supplier: '', price: '', qty: '1' }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function NewEstimate() {
  const { t } = useTranslation('estimates')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // ─── Customer state ───────────────────────────────────────────────
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerOpen, setCustomerOpen] = useState(false)
  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [newCustomerPhone, setNewCustomerPhone] = useState('')
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)
  const customerBoxRef = useRef<HTMLDivElement | null>(null)

  // ─── VIN state ────────────────────────────────────────────────────
  const [vin, setVin] = useState('')

  // ─── Form state ───────────────────────────────────────────────────
  const [workDescription, setWorkDescription] = useState('')
  const [parts, setParts] = useState<PartRow[]>([emptyPartRow()])
  const [laborHours, setLaborHours] = useState('')
  const [laborRate, setLaborRate] = useState(DEFAULT_LABOR_RATE.toString())
  const [submitError, setSubmitError] = useState<string | null>(null)

  // ─── Parts search Sheet state ─────────────────────────────────────
  // PoC: eBay-only. The Sheet pre-fills its keyword from the current
  // work description so the mechanic doesn't retype the job they just
  // described one section up.
  const [partsSearchOpen, setPartsSearchOpen] = useState(false)
  const [partsSearchQuery, setPartsSearchQuery] = useState('')
  const [partsSearchSubmitted, setPartsSearchSubmitted] = useState('')
  const [partsAddedIds, setPartsAddedIds] = useState<Set<string>>(new Set())

  // Close the customer combobox on outside click.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        customerBoxRef.current &&
        !customerBoxRef.current.contains(e.target as Node)
      ) {
        setCustomerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const customersQuery = useQuery({
    queryKey: ['customers'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as CustomerListRow[]
    },
  })

  const filteredCustomers = useMemo(() => {
    const all = customersQuery.data ?? []
    const q = customerSearch.trim().toLowerCase()
    if (!q) return all.slice(0, 8)
    return all
      .filter((c) => {
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q)
        )
      })
      .slice(0, 8)
  }, [customersQuery.data, customerSearch])

  const selectedCustomer = useMemo(() => {
    if (!customerId) return null
    return (customersQuery.data ?? []).find((c) => c.id === customerId) ?? null
  }, [customerId, customersQuery.data])

  // ─── VIN decode trigger ──────────────────────────────────────────
  // Fire only on a complete (17-char) VIN, debounced. Earlier keystrokes
  // would just cause flicker. Pattern C (TanStack useQuery): isFetching
  // is the "decoding" indicator and error supplies the error string —
  // no setState-in-effect kickoff.
  const trimmedVinDecode = vin.trim().toUpperCase()
  const vinIsValid = isValidVin(trimmedVinDecode)
  const vinDecodeQuery = useQuery({
    queryKey: ['vin-decode', trimmedVinDecode],
    enabled: vinIsValid,
    queryFn: ({ signal }) => decodeVin(trimmedVinDecode, { signal }),
  })
  const decoded: DecodedVin | null =
    vinIsValid && vinDecodeQuery.data ? vinDecodeQuery.data : null
  const vinDecoding = vinIsValid && vinDecodeQuery.isFetching
  const vinError =
    vinIsValid && vinDecodeQuery.error
      ? vinDecodeQuery.error instanceof Error
        ? vinDecodeQuery.error.message
        : t('new.vin.error_generic')
      : null

  // ─── Computed totals ─────────────────────────────────────────────
  const partsSubtotal = useMemo(() => {
    return round2(
      parts.reduce((sum, p) => {
        const price = Number.parseFloat(p.price) || 0
        const qty = Number.parseInt(p.qty, 10) || 0
        return sum + price * qty
      }, 0),
    )
  }, [parts])

  const laborSubtotal = useMemo(() => {
    const h = Number.parseFloat(laborHours) || 0
    const r = Number.parseFloat(laborRate) || 0
    return round2(h * r)
  }, [laborHours, laborRate])

  const subtotal = round2(partsSubtotal + laborSubtotal)
  const tax = round2(subtotal * HST_RATE)
  const total = round2(subtotal + tax)

  // ─── Submit ──────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      // Filter empty rows — a row with no name AND no price is treated
      // as not-yet-filled and dropped silently.
      const cleanParts = parts
        .filter((p) => p.name.trim().length > 0)
        .map((p) => ({
          name: p.name.trim(),
          supplier: p.supplier.trim() || null,
          price: Number.parseFloat(p.price) || 0,
          qty: Math.max(1, Number.parseInt(p.qty, 10) || 1),
        }))

      const payload = {
        customer_id: customerId,
        work_description: workDescription.trim(),
        vin: vin.trim().toUpperCase() || null,
        vehicle_year: decoded?.year ?? null,
        vehicle_make: decoded?.make ?? null,
        vehicle_model: decoded?.model ?? null,
        vehicle_engine: decoded?.engine ?? null,
        parts: cleanParts,
        labor_hours: Number.parseFloat(laborHours) || 0,
        labor_rate: Number.parseFloat(laborRate) || 0,
      }

      const res = await fetch('/api/estimates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { id: string }
    },
    onSuccess: (data) => {
      // Bust the list cache so the new draft appears on /estimates.
      queryClient.invalidateQueries({ queryKey: ['estimates'] })
      navigate(`/estimates/${data.id}`)
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : t('new.error_submit'))
    },
  })

  // ─── Customer creation ───────────────────────────────────────────
  async function handleCreateCustomer() {
    setCustomerError(null)
    if (!newCustomerName.trim()) {
      setCustomerError(t('new.customer.name_required'))
      return
    }
    setCreatingCustomer(true)
    try {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newCustomerName.trim(),
          phone: newCustomerPhone.trim() || null,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      const created = (await res.json()) as CustomerListRow
      // Refresh the customers cache so the picker shows the new row,
      // then auto-select it.
      await queryClient.invalidateQueries({ queryKey: ['customers'] })
      setCustomerId(created.id)
      setCustomerSearch(created.name)
      setShowNewCustomer(false)
      setCustomerOpen(false)
      setNewCustomerName('')
      setNewCustomerPhone('')
    } catch (err) {
      setCustomerError(
        err instanceof Error ? err.message : t('new.customer.create_failed'),
      )
    } finally {
      setCreatingCustomer(false)
    }
  }

  // ─── Validation ──────────────────────────────────────────────────
  const canSubmit =
    !!customerId &&
    workDescription.trim().length > 0 &&
    !createMutation.isPending

  // ─── Parts row mutators ──────────────────────────────────────────
  function updatePart(rowKey: string, patch: Partial<PartRow>) {
    setParts((rows) =>
      rows.map((r) => (r.rowKey === rowKey ? { ...r, ...patch } : r)),
    )
  }

  function removePart(rowKey: string) {
    setParts((rows) => {
      const next = rows.filter((r) => r.rowKey !== rowKey)
      // Always keep at least one (empty) row so the UI doesn't collapse.
      return next.length > 0 ? next : [emptyPartRow()]
    })
  }

  function addPart() {
    setParts((rows) => [...rows, emptyPartRow()])
  }

  // ─── Parts search ────────────────────────────────────────────────
  // Each Sheet open starts a fresh "added" set so re-opening doesn't
  // remember previous-session adds. The query itself only fires when
  // `partsSearchSubmitted` has a value (Enter or Search button).
  const partsSearchResultsQuery = useQuery({
    queryKey: ['parts-search', partsSearchSubmitted],
    enabled: isLoaded && partsSearchSubmitted.trim().length >= 2,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const params = new URLSearchParams({ q: partsSearchSubmitted, limit: '15' })
      const res = await fetch(`/api/parts/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as PartsSearchResponse
    },
  })

  function openPartsSearch() {
    setPartsSearchQuery(workDescription.trim())
    setPartsSearchSubmitted('')
    setPartsAddedIds(new Set())
    setPartsSearchOpen(true)
  }

  function runPartsSearch() {
    const q = partsSearchQuery.trim()
    if (q.length < 2) return
    setPartsSearchSubmitted(q)
  }

  function addPartFromResult(r: PartsSearchResult) {
    // Replace the first empty row if there is one, else append. Avoids
    // leaving a stray blank row at the bottom after the mechanic clicks
    // Add on a fresh estimate.
    const supplierLabel = r.seller
      ? `eBay: ${r.seller}`
      : r.is_sandbox
      ? 'eBay (sandbox)'
      : 'eBay'
    const priceText = r.price === null ? '' : r.price.toString()
    const newRow: PartRow = {
      rowKey: newRowKey(),
      name: r.brand ? `${r.brand} ${r.title}` : r.title,
      supplier: supplierLabel,
      price: priceText,
      qty: '1',
    }
    setParts((rows) => {
      const firstEmptyIdx = rows.findIndex(
        (row) =>
          row.name.trim() === '' &&
          row.supplier.trim() === '' &&
          row.price.trim() === '',
      )
      if (firstEmptyIdx === -1) return [...rows, newRow]
      return rows.map((row, idx) =>
        idx === firstEmptyIdx ? { ...newRow, rowKey: row.rowKey } : row,
      )
    })
    setPartsAddedIds((prev) => {
      const next = new Set(prev)
      next.add(r.ebay_item_id)
      return next
    })
  }

  // ─── Submit handler ──────────────────────────────────────────────
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (!customerId) {
      setSubmitError(t('new.error.no_customer'))
      return
    }
    if (workDescription.trim().length === 0) {
      setSubmitError(t('new.error.no_work'))
      return
    }
    createMutation.mutate()
  }

  return (
    <div className="mx-auto w-full max-w-[660px] space-y-6 lg:max-w-[720px]">
      {/* §4.5 detail header — 44px back-button row above the 22px title.
          The "←" glyph lives in the i18n string. */}
      <div>
        <Link
          to="/estimates"
          className="-ml-2 inline-flex min-h-11 items-center px-2 text-sm text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          {t('new.back')}
        </Link>
        <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
          {t('new.title')}
        </h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{t('new.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ─── Customer ──────────────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-card p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('new.customer.header')}
          </div>

          <div className="space-y-2" ref={customerBoxRef}>
            <Label htmlFor="customer-search">{t('new.customer.label')}</Label>
            {selectedCustomer ? (
              // Neutral surface strip — a picked customer is a form
              // selection, not a system save (§2: green never decorates).
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-border bg-muted p-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-foreground">
                    {selectedCustomer.name}
                  </div>
                  <div className="font-mono text-xs tabular-nums text-muted-foreground">
                    <PhoneLink phone={selectedCustomer.phone} />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="pill"
                  onClick={() => {
                    setCustomerId(null)
                    setCustomerSearch('')
                    setCustomerOpen(true)
                  }}
                >
                  {t('new.customer.change')}
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="customer-search"
                  type="search"
                  autoComplete="off"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value)
                    setCustomerOpen(true)
                  }}
                  onFocus={() => setCustomerOpen(true)}
                  placeholder={t('new.customer.placeholder')}
                  className="w-full sm:w-96"
                />
                {customerOpen ? (
                  <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-[10px] border border-border bg-popover shadow-2 sm:w-96">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-muted-foreground">
                        {t('new.customer.no_results')}
                      </div>
                    ) : (
                      filteredCustomers.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => {
                            setCustomerId(c.id)
                            setCustomerSearch(c.name)
                            setCustomerOpen(false)
                          }}
                          className="flex min-h-11 w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-[background-color] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                        >
                          <span className="text-sm font-medium text-foreground">
                            {c.name}
                          </span>
                          <span className="font-mono text-xs tabular-nums text-muted-foreground">
                            {formatPhone(c.phone)}
                          </span>
                        </button>
                      ))
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCustomer(true)
                        setCustomerOpen(false)
                        if (customerSearch.trim().length > 0) {
                          setNewCustomerName(customerSearch.trim())
                        }
                      }}
                      className="flex min-h-11 w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm font-medium text-foreground transition-[background-color] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                    >
                      <Plus className="size-4" />
                      {t('new.customer.create_new')}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {showNewCustomer ? (
            <div className="mt-3 space-y-3 rounded-[10px] border border-dashed border-border bg-muted/60 p-3">
              <div className="text-[13px] font-semibold text-muted-foreground">
                {t('new.customer.new_header')}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="new-customer-name">
                    {t('new.customer.name')}
                  </Label>
                  <Input
                    id="new-customer-name"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    placeholder={t('new.customer.name_placeholder')}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="new-customer-phone">
                    {t('new.customer.phone')}
                  </Label>
                  <Input
                    id="new-customer-phone"
                    type="tel"
                    value={newCustomerPhone}
                    onChange={(e) => setNewCustomerPhone(e.target.value)}
                    placeholder={t('new.customer.phone_placeholder')}
                  />
                </div>
              </div>
              {customerError ? (
                <div className="text-xs text-destructive">{customerError}</div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="pill"
                  onClick={handleCreateCustomer}
                  disabled={creatingCustomer}
                >
                  {creatingCustomer
                    ? tCommon('loading')
                    : t('new.customer.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="pill"
                  onClick={() => {
                    setShowNewCustomer(false)
                    setCustomerError(null)
                  }}
                >
                  {t('new.customer.cancel')}
                </Button>
              </div>
            </div>
          ) : null}
        </section>

        {/* ─── Vehicle / VIN ─────────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-card p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('new.vehicle.header')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="vin">{t('new.vehicle.vin_label')}</Label>
            <Input
              id="vin"
              value={vin}
              onChange={(e) =>
                setVin(e.target.value.toUpperCase().replace(/\s+/g, ''))
              }
              placeholder={t('new.vehicle.vin_placeholder')}
              maxLength={17}
              className="w-full font-mono uppercase tabular-nums sm:w-96"
            />
            {vinDecoding ? (
              <div className="text-xs text-muted-foreground">
                {t('new.vehicle.decoding')}
              </div>
            ) : null}
            {vinError ? (
              <div className="text-xs text-destructive">{vinError}</div>
            ) : null}
            {decoded ? (
              // Neutral surface — a decode is form assistance, nothing is
              // saved yet, so no green (§2).
              <div className="mt-2 grid gap-2 rounded-[10px] border border-border bg-muted p-3 text-sm sm:grid-cols-4">
                <DecodedField
                  label={t('new.vehicle.year')}
                  value={decoded.year ? String(decoded.year) : null}
                />
                <DecodedField
                  label={t('new.vehicle.make')}
                  value={decoded.make}
                />
                <DecodedField
                  label={t('new.vehicle.model')}
                  value={decoded.model}
                />
                <DecodedField
                  label={t('new.vehicle.engine')}
                  value={decoded.engine}
                />
              </div>
            ) : null}
          </div>
        </section>

        {/* ─── Job description ───────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-card p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('new.job.header')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="work-description">{t('new.job.label')}</Label>
            <Textarea
              id="work-description"
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              placeholder={t('new.job.placeholder')}
              rows={3}
            />
          </div>
        </section>

        {/* ─── Parts table ───────────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-card p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-[13px] font-semibold text-muted-foreground">
              {t('new.parts.header')}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="pill"
                onClick={openPartsSearch}
              >
                <Search className="size-4" />
                {t('new.parts.search')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="pill"
                onClick={addPart}
              >
                <Plus className="size-4" />
                {t('new.parts.add')}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {parts.map((p, idx) => {
              const lineTotal =
                (Number.parseFloat(p.price) || 0) *
                (Number.parseInt(p.qty, 10) || 0)
              return (
                <div
                  key={p.rowKey}
                  className="grid gap-2 rounded-[10px] border border-border p-3 sm:grid-cols-[2fr_1fr_1fr_0.7fr_auto]"
                >
                  <Input
                    aria-label={t('new.parts.name')}
                    placeholder={t('new.parts.name_placeholder')}
                    value={p.name}
                    onChange={(e) =>
                      updatePart(p.rowKey, { name: e.target.value })
                    }
                  />
                  <Input
                    aria-label={t('new.parts.supplier')}
                    placeholder={t('new.parts.supplier_placeholder')}
                    value={p.supplier}
                    onChange={(e) =>
                      updatePart(p.rowKey, { supplier: e.target.value })
                    }
                  />
                  <Input
                    aria-label={t('new.parts.price')}
                    placeholder={t('new.parts.price_placeholder')}
                    value={p.price}
                    onChange={(e) =>
                      updatePart(p.rowKey, { price: e.target.value })
                    }
                    inputMode="decimal"
                  />
                  <Input
                    aria-label={t('new.parts.qty')}
                    value={p.qty}
                    onChange={(e) =>
                      updatePart(p.rowKey, { qty: e.target.value })
                    }
                    inputMode="numeric"
                  />
                  <div className="flex items-center justify-between gap-2 sm:justify-end">
                    <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                      {formatMoney(lineTotal)}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('new.parts.remove')}
                      onClick={() => removePart(p.rowKey)}
                      disabled={parts.length === 1 && idx === 0}
                      className="size-11"
                    >
                      <Trash2 className="size-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* ─── Labor ─────────────────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-card p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('new.labor.header')}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="labor-hours">{t('new.labor.hours')}</Label>
              <Input
                id="labor-hours"
                value={laborHours}
                onChange={(e) => setLaborHours(e.target.value)}
                inputMode="decimal"
                placeholder="0.0"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="labor-rate">{t('new.labor.rate')}</Label>
              <Input
                id="labor-rate"
                value={laborRate}
                onChange={(e) => setLaborRate(e.target.value)}
                inputMode="decimal"
                placeholder={DEFAULT_LABOR_RATE.toString()}
              />
            </div>
          </div>
        </section>

        {/* ─── Totals ────────────────────────────────────────────── */}
        {/* Neutral surface — a running total is not a system save (§2). */}
        <section className="rounded-[14px] border border-border bg-muted p-4 sm:p-5">
          <div className="space-y-1.5 text-sm">
            <TotalsRow label={t('new.totals.parts')} value={partsSubtotal} />
            <TotalsRow label={t('new.totals.labor')} value={laborSubtotal} />
            <TotalsRow label={t('new.totals.subtotal')} value={subtotal} />
            <TotalsRow label={t('new.totals.tax')} value={tax} />
            <div className="flex justify-between border-t border-input pt-1.5 text-base font-bold text-foreground">
              <span>{t('new.totals.total')}</span>
              <span className="font-mono tabular-nums">{formatMoney(total)}</span>
            </div>
          </div>
        </section>

        {submitError ? (
          <div className="rounded-[10px] border border-destructive/25 bg-destructive-soft p-3 text-sm text-destructive">
            {submitError}
          </div>
        ) : null}

        {/*
          Sticky action footer on mobile so the Save button stays reachable
          without scrolling past the parts list / labor matrix on a phone.
          On md: and up, falls back to inline-at-the-end-of-form layout.
          The pb-[max(...)] adds the iPhone home-indicator safe area without
          inflating Android padding. See CLAUDE.md § "Responsive design
          conventions" — the sticky action bar pattern.
        */}
        <div className="sticky bottom-[var(--shell-bottom-inset,0px)] -mx-4 border-t border-border bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur supports-[backdrop-filter]:bg-background/80 md:static md:mx-0 md:border-t-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Link
              to="/estimates"
              className="inline-flex min-h-11 items-center px-2 text-sm text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
            >
              {t('new.cancel')}
            </Link>
            <Button type="submit" size="pill" disabled={!canSubmit}>
              {createMutation.isPending
                ? tCommon('loading')
                : t('new.save')}
            </Button>
          </div>
        </div>
      </form>

      {/* ─── Parts search Sheet ────────────────────────────────────── */}
      <Sheet open={partsSearchOpen} onOpenChange={setPartsSearchOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
        >
          <SheetHeader className="border-b border-border px-6 py-5 pr-12">
            <SheetTitle>{t('new.parts.search_sheet.title')}</SheetTitle>
            <SheetDescription>
              {t('new.parts.search_sheet.subtitle')}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 px-6 py-5">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <Label htmlFor="parts-search-input">
                  {t('new.parts.search_sheet.label')}
                </Label>
                <Input
                  id="parts-search-input"
                  value={partsSearchQuery}
                  placeholder={t('new.parts.search_sheet.placeholder')}
                  onChange={(e) => setPartsSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      runPartsSearch()
                    }
                  }}
                />
              </div>
              <Button
                type="button"
                size="pill"
                onClick={runPartsSearch}
                disabled={partsSearchQuery.trim().length < 2}
              >
                <Search className="size-4" />
                {t('new.parts.search_sheet.go')}
              </Button>
            </div>

            {partsSearchResultsQuery.data?.env === 'sandbox' ? (
              // Neutral, not amber — §2.4: amber marks a decision waiting
              // on you, never an informational notice.
              <div className="rounded-[10px] border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
                {t('new.parts.search_sheet.sandbox_notice')}
              </div>
            ) : null}

            {partsSearchResultsQuery.isFetching ? (
              <div className="text-sm text-muted-foreground">
                {tCommon('loading')}
              </div>
            ) : null}

            {partsSearchResultsQuery.isError ? (
              <div className="rounded-[10px] border border-destructive/25 bg-destructive-soft px-3 py-2 text-sm text-destructive">
                {partsSearchResultsQuery.error instanceof Error
                  ? partsSearchResultsQuery.error.message
                  : t('new.parts.search_sheet.error')}
              </div>
            ) : null}

            {partsSearchResultsQuery.data &&
            partsSearchResultsQuery.data.results.length === 0 ? (
              <div className="rounded-[10px] border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                {t('new.parts.search_sheet.empty')}
              </div>
            ) : null}

            <ul className="space-y-2">
              {(partsSearchResultsQuery.data?.results ?? []).map((r) => {
                const added = partsAddedIds.has(r.ebay_item_id)
                return (
                  <li
                    key={r.ebay_item_id}
                    className="flex flex-wrap items-start justify-between gap-3 rounded-[10px] border border-border p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-2 text-sm font-medium text-foreground">
                        {r.title}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {r.condition ? (
                          <span className="mr-2">[{r.condition}]</span>
                        ) : null}
                        {r.price !== null ? (
                          <span className="mr-2 font-mono tabular-nums">
                            {r.price.toFixed(2)} {r.currency}
                          </span>
                        ) : null}
                        {r.shipping_cost !== null ? (
                          <span className="mr-2">
                            {t('new.parts.search_sheet.shipping', {
                              cost: r.shipping_cost.toFixed(2),
                              currency: r.currency,
                            })}
                          </span>
                        ) : null}
                        {r.seller ? (
                          <span>
                            {t('new.parts.search_sheet.seller', {
                              seller: r.seller,
                            })}
                          </span>
                        ) : null}
                      </div>
                      {r.item_url ? (
                        <a
                          href={r.item_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-block text-xs font-medium text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                        >
                          {t('new.parts.search_sheet.view')}
                        </a>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant={added ? 'outline' : 'default'}
                      size="pill"
                      onClick={() => addPartFromResult(r)}
                      disabled={added}
                    >
                      {added ? (
                        <>
                          <Check className="size-4" />
                          {t('new.parts.search_sheet.added')}
                        </>
                      ) : (
                        <>
                          <Plus className="size-4" />
                          {t('new.parts.search_sheet.add')}
                        </>
                      )}
                    </Button>
                  </li>
                )
              })}
            </ul>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function TotalsRow({
  label,
  value,
}: {
  label: string
  value: number
}) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="font-mono tabular-nums">{formatMoney(value)}</span>
    </div>
  )
}

function DecodedField({
  label,
  value,
}: {
  label: string
  value: string | null
}) {
  return (
    <div>
      <div className="text-[12px] font-medium text-ink-3">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium text-foreground">
        {value ?? '—'}
      </div>
    </div>
  )
}
